import { useId } from 'react'

import { tenantDay } from '@/api/overview'
import type { SignupsReport } from '@/api/signups'
import type { PeriodInfo } from '@/api/types'
import { Card, formatInteger, formatPercent, KpiCard, TrendChart } from '@/design'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import { useSignups } from '@/features/overview/hooks'
import { dayCount, formatDateKey, formatWindow } from '@/lib/filters'

import { formatDay } from '../list/cells'
import { SelfReportedSection } from './SelfReportedTable'

/** The previous period the trend's dashed line covers, named by its own dates; none while comparison is off. */
function previousWindowLabel(period: PeriodInfo | undefined): string | null {
  const from = period?.previousFrom
  const to = period?.previousTo
  return from && to ? formatWindow({ from, to, days: dayCount(from, to) }) : null
}

const NOT_MEASURED = 'Not measured'

/** The four headline figures: sign-ups against the previous period, conversion to a first order, and what measured visitors did. */
function SignupsKpis({ report, compareLabel }: { report: SignupsReport; compareLabel: string | null }) {
  const { totals, converted, measured } = report
  const comparing = compareLabel !== null
  const measuredNote = measured ? 'Measured sessions' : 'No measured visit before this period ended'
  return (
    <ul className="cus-signup-kpis" aria-label="Sign-up figures">
      <li>
        <KpiCard
          label="Sign-ups"
          value={formatInteger(totals.signups)}
          change={comparing ? totals.change : null}
          polarity="up_good"
          comparisonLabel={compareLabel}
          note={comparing && totals.change === null ? 'None in the previous period' : undefined}
          detail="Accounts created on the website"
        />
      </li>
      <li>
        <KpiCard
          label="Went on to buy"
          value={formatInteger(converted.count)}
          change={null}
          polarity="up_good"
          note={converted.rate === null ? 'No sign-ups to convert' : `${formatPercent(converted.rate)} of sign-ups`}
          detail="First order before the period ended"
        />
      </li>
      <li>
        <KpiCard
          label="Registration form shown"
          value={measured ? formatInteger(measured.registrationStarted) : NOT_MEASURED}
          change={null}
          polarity="neutral"
          note={measuredNote}
        />
      </li>
      <li>
        <KpiCard
          label="Accounts created, measured"
          value={measured ? formatInteger(measured.registrationCompleted) : NOT_MEASURED}
          change={null}
          polarity="up_good"
          note={measuredNote}
          detail={measured && measured.pendingSync > 0 ? `${formatInteger(measured.pendingSync)} waiting for their customer to sync` : undefined}
        />
      </li>
    </ul>
  )
}

interface Bar {
  key: string
  label: string
  count: number
  hint?: string
}

/**
 * How the period's sign-ups came: each measured channel, then the measured sign-ups not
 * yet placed and the unmeasured ones, always shown, so the bars add up to the total.
 */
function ChannelSplit({ report }: { report: SignupsReport }) {
  const headingId = useId()
  const total = report.totals.signups
  const bars: Bar[] = [
    ...report.byChannel.map((entry) => ({ key: entry.channel, label: entry.label, count: entry.signups })),
    {
      key: 'unattributed',
      label: 'Unattributed',
      count: report.unattributed,
      hint: 'Measured, but the visit’s channel is placed by the nightly attribution run',
    },
    {
      key: 'unmeasured',
      label: 'Unmeasured',
      count: report.unmeasured,
      hint: 'No measured visit: analytics declined, an account made in wp-admin, or before tracking began',
    },
  ]
  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="cus-subheading">
        How they came
      </h3>
      <ul className="cus-bars">
        {bars.map((bar) => {
          const share = total > 0 ? bar.count / total : 0
          return (
            <li key={bar.key} data-channel={bar.key} className="cus-bar">
              <span className="cus-bar__label">
                {bar.label}
                {bar.hint ? <span className="list-cell-sub">{bar.hint}</span> : null}
              </span>
              <span className="cus-bar__track" aria-hidden="true">
                <span className="cus-bar__fill" style={{ width: `${Math.round(share * 100)}%` }} />
              </span>
              <span className="cus-bar__value">
                {formatInteger(bar.count)}
                {total > 0 ? <span className="list-muted"> · {formatPercent(share, 0)}</span> : null}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** What the event figures can see: the API's sentence, and since when, when that is part of the period or none of it. */
function Coverage({ report, period }: { report: SignupsReport; period: PeriodInfo | undefined }) {
  const { coverage, measured, timezone } = report
  const partial =
    measured !== null &&
    coverage.measuredFrom !== null &&
    period !== undefined &&
    tenantDay(coverage.measuredFrom, timezone) > period.from
  return (
    <div className="list-notes" data-testid="signups-coverage">
      <p className="list-note">{coverage.note}</p>
      {measured === null ? (
        <p className="list-note" role="note">
          <strong>Not measured:</strong> browser tracking had not started by the end of this period, so every sign-up is
          unmeasured and the form figures are empty.
        </p>
      ) : null}
      {partial && coverage.measuredFrom ? (
        <p className="list-note" role="note">
          <strong>Measured from {formatDay(coverage.measuredFrom, timezone, undefined)}:</strong> the channel split and the
          form figures cover only part of this period.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The period's sign-ups (`GET /analytics/signups`): the headline figures, sign-ups per
 * day against the previous period, the channels they came from, what the event
 * figures can see, and apart from those, what the customers themselves said brought them. Its figures are the Overview's Sign-ups tile's, read once for both.
 */
export function SignupsPanel() {
  const signups = useSignups()
  const period = signups.data?.meta.period
  return (
    <Card
      title="Sign-ups"
      subtitle={period ? `${period.label} · accounts created on the website` : undefined}
      busy={signups.isFetching}
    >
      <CardQueryBody query={signups} loadingRows={4} loadingLabel="Loading sign-ups" errorDescription="Sign-ups could not be loaded just now.">
        {({ data: report, meta }) => {
          const compareLabel = meta.period?.compareLabel ?? null
          const comparing = compareLabel !== null
          return (
            <div className="cus-signups">
              <SignupsKpis report={report} compareLabel={compareLabel} />
              {report.points.length > 1 ? (
                <TrendChart
                  title="Sign-ups per day"
                  points={report.points.map((point, index) => ({
                    label: formatDateKey(point.date),
                    current: point.signups,
                    previous: comparing ? (report.previousPoints[index]?.signups ?? null) : null,
                  }))}
                  format="integer"
                  currentLabel={meta.period?.label ?? 'This period'}
                  previousLabel={comparing ? previousWindowLabel(meta.period) : null}
                  height={180}
                />
              ) : null}
              <ChannelSplit report={report} />
              <Coverage report={report} period={meta.period} />
              <SelfReportedSection report={report} />
            </div>
          )
        }}
      </CardQueryBody>
    </Card>
  )
}
